import { useEffect, useMemo, useState } from "react";
import { Pencil, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { TRY } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { IMG } from "@/components/pos/PosBits";
import { KDV_DEFAULT, PAY_OPTIONS, r2, paymentsSummary } from "@/lib/pos";
import { unitGrossOf } from "@/lib/receipt";

const grossFrom = (net, kdv) => r2((Number(net) || 0) * (1 + (Number(kdv) || 0) / 100));
const unitGross = (l) => r2(l.unit_gross != null ? l.unit_gross : (Number(l.unit_net) || 0) * (1 + (Number(l.kdv_rate) || 0) / 100));
const fromSale = (s) => ({
  id: s.id, title: s.title || "", note: s.note || "", payment_method: s.payment_method || "nakit", payments: s.payments || [],
  lines: (s.items || []).map((it) => ({
    product_id: it.product_id, name: it.name, code: it.code || "", barcode: it.barcode || "", image_url: it.image_url || "",
    unit: it.unit || "ADET", quantity: Number(it.quantity) || 0, list_price: Number(it.list_price) || 0,
    sale_discount: Number(it.sale_discount) || 0, unit_net: Number(it.unit_net) || 0, unit_gross: unitGrossOf(it), kdv_rate: Number(it.kdv_rate) || KDV_DEFAULT,
  })),
});

// Tamamlanmış hızlı satışı düzenleme (yönetici): kalem adet/iskonto/net/KDV + başlık, not, ödeme yöntemi
export function EditSaleDialog({ sale, onClose, onSaved }) {
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setEdit(sale ? fromSale(sale) : null); }, [sale]);

  const setLine = (pid, patch) => setEdit((e) => ({ ...e, lines: e.lines.map((l) => (l.product_id === pid ? { ...l, ...patch } : l)) }));
  const onDisc = (pid, v) => { const l = edit.lines.find((x) => x.product_id === pid); const net = r2(l.list_price * (1 - (Number(v) || 0) / 100)); setLine(pid, { sale_discount: v, unit_net: net, unit_gross: grossFrom(net, l.kdv_rate) }); };
  const onNet = (pid, v) => { const l = edit.lines.find((x) => x.product_id === pid); const lp = l.list_price; setLine(pid, { unit_net: v, unit_gross: grossFrom(v, l.kdv_rate), sale_discount: lp > 0 ? r2((1 - (Number(v) || 0) / lp) * 100) : 0 }); };
  const onKdv = (pid, v) => { const l = edit.lines.find((x) => x.product_id === pid); setLine(pid, { kdv_rate: v, unit_gross: grossFrom(l.unit_net, v) }); };
  const rm = (pid) => setEdit((e) => ({ ...e, lines: e.lines.filter((l) => l.product_id !== pid) }));
  const totals = useMemo(() => {
    if (!edit) return { sub: 0, kdv: 0, grand: 0 };
    let sub = 0, kdv = 0, grand = 0;
    edit.lines.forEach((l) => { const lg = r2(unitGross(l) * (Number(l.quantity) || 0)); const ln = r2(lg / (1 + (Number(l.kdv_rate) || 0) / 100)); sub += ln; kdv += r2(lg - ln); grand += lg; });
    return { sub: r2(sub), kdv: r2(kdv), grand: r2(grand) };
  }, [edit]);

  const save = async () => {
    const items = edit.lines.filter((l) => (Number(l.quantity) || 0) > 0).map((l) => ({
      product_id: l.product_id, name: l.name, code: l.code, barcode: l.barcode, image_url: l.image_url,
      quantity: Number(l.quantity), list_price: Number(l.list_price) || 0, sale_discount: Number(l.sale_discount) || 0,
      unit_net: Number(l.unit_net) || 0, unit_gross: unitGross(l), kdv_rate: Number(l.kdv_rate) || 0,
    }));
    if (!items.length) return toast.error("En az bir ürün kalmalı");
    setSaving(true);
    try {
      await api.put(`/quick-sales/${edit.id}`, { items, title: edit.title.trim(), note: (edit.note || "").trim(), payment_method: edit.payment_method });
      toast.success("Satış güncellendi · stok farkı düzeltildi");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!sale} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[98vw] sm:max-w-3xl max-h-[94vh] overflow-y-auto" data-testid="qs-edit-dialog">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><Pencil className="h-5 w-5 text-primary" />Hızlı Satışı Düzenle</DialogTitle></DialogHeader>
        {edit && (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Başlık / Müşteri Adı</Label>
                <Input className="mt-1" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="qs-edit-title" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Not / Açıklama (fişte görünür)</Label>
                <Input className="mt-1" value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} maxLength={500} placeholder="Opsiyonel" data-testid="qs-edit-note" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-muted-foreground">Ödeme Yöntemi</Label>
                {edit.payment_method === "karma" ? (
                  <div className="mt-1 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="qs-edit-pay-karma">
                    <b>Parçalı ödeme:</b> {paymentsSummary(edit.payments)}<br /><span className="text-[11px] text-sky-800/80">Kalem değişirse fark son nakdi parçaya yansır. Dağılımı değiştirmek için satışı iptal edip yeniden yapın.</span>
                  </div>
                ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-1">
                  {PAY_OPTIONS.filter(([val]) => val !== "veresiye" || String(edit.title || "").trim() || edit.payment_method === "veresiye").map(([val, lbl, Icon]) => (
                    <button key={val} onClick={() => setEdit({ ...edit, payment_method: val })} data-testid={`qs-edit-pay-${val}`}
                      className={`flex items-center justify-center gap-1 rounded-lg border p-2 text-xs transition-colors ${edit.payment_method === val ? "border-primary bg-primary/5 text-primary font-medium" : "border-border"}`}>
                      <Icon className="h-3.5 w-3.5" />{lbl}
                    </button>
                  ))}
                </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              {edit.lines.map((l) => (
                <div key={l.product_id} className="border rounded-lg p-2.5" data-testid={`qs-edit-line-${l.product_id}`}>
                  <div className="flex items-start gap-2 mb-2">
                    <IMG src={l.image_url} alt={l.name} className="h-9 w-9" />
                    <div className="min-w-0 flex-1"><div className="font-medium text-sm leading-tight break-words">{l.name}</div><div className="text-[11px] text-muted-foreground">{l.code}</div></div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => rm(l.product_id)} data-testid={`qs-edit-rm-${l.product_id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Adet</Label>
                      <Input className="h-8 mt-0.5 tabular-nums" type="number" value={l.quantity} onChange={(e) => setLine(l.product_id, { quantity: e.target.value })} data-testid={`qs-edit-qty-${l.product_id}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">İskonto %</Label>
                      <Input className="h-8 mt-0.5 tabular-nums" type="number" value={l.sale_discount} onChange={(e) => onDisc(l.product_id, e.target.value)} data-testid={`qs-edit-disc-${l.product_id}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">Net Fiyat</Label>
                      <Input className="h-8 mt-0.5 tabular-nums" type="number" value={l.unit_net} onChange={(e) => onNet(l.product_id, e.target.value)} data-testid={`qs-edit-net-${l.product_id}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-muted-foreground">KDV %</Label>
                      <Input className="h-8 mt-0.5 tabular-nums" type="number" value={l.kdv_rate} onChange={(e) => onKdv(l.product_id, e.target.value)} data-testid={`qs-edit-kdv-${l.product_id}`} />
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground mt-1.5">Satış (KDV dahil): <b className="text-primary text-sm">{TRY(unitGross(l))}</b> / birim</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col items-end gap-1 pt-1">
              <div className="flex justify-between w-full sm:w-72 text-sm"><span className="text-muted-foreground">Ara Toplam</span><span className="tabular-nums">{TRY(totals.sub)}</span></div>
              <div className="flex justify-between w-full sm:w-72 text-sm"><span className="text-muted-foreground">KDV</span><span className="tabular-nums">{TRY(totals.kdv)}</span></div>
              <div className="flex justify-between w-full sm:w-72 text-lg font-bold border-t pt-1"><span>Genel Toplam</span><span className="tabular-nums text-primary" data-testid="qs-edit-grand">{TRY(totals.grand)}</span></div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} data-testid="qs-edit-cancel">Vazgeç</Button>
              <Button onClick={save} disabled={saving} data-testid="qs-edit-save"><Check className="h-4 w-4 mr-1.5" />{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
